import type { Campaign, CampaignStatus } from '../types/api';

/**
 * The campaign list shows the language versions of one send as one row: a
 * campaign named "Halloween 2026 (DE)" belongs with "Halloween 2026 (DA)".
 * Only the name links them (there is no group in the data model), so a
 * campaign renamed out of the pattern simply shows on its own again.
 */
const LANGUAGE_SUFFIX = /^(.*\S)\s+\(([A-Z]{2})\)$/;

/** Shown first, in this order; any other code follows alphabetically. */
const LANGUAGE_ORDER = ['DA', 'DE', 'EN', 'FR', 'IT', 'NL'];

export interface CampaignGroup {
  /** The name without the language, e.g. "Halloween 2026". */
  name: string;
  /** The language versions, in `LANGUAGE_ORDER`. */
  members: { language: string; campaign: Campaign }[];
  /** The newest member's creation time, so the group sorts like a campaign. */
  createdAt: number;
}

export type CampaignListEntry =
  { kind: 'single'; campaign: Campaign } | { kind: 'group'; group: CampaignGroup };

export function splitLanguage(name: string): { name: string; language: string } | null {
  const m = LANGUAGE_SUFFIX.exec(name.trim());
  return m ? { name: m[1]!, language: m[2]! } : null;
}

function languageRank(code: string): number {
  const i = LANGUAGE_ORDER.indexOf(code);
  return i === -1 ? LANGUAGE_ORDER.length : i;
}

/**
 * Groups campaigns whose names differ only in the language at the end.
 * A name with a language but no siblings stays a single row. Entries keep
 * the order of `campaigns` (each group sits where its first member was).
 */
export function groupCampaigns(campaigns: Campaign[]): CampaignListEntry[] {
  const byName = new Map<string, CampaignGroup>();
  for (const c of campaigns) {
    const split = splitLanguage(c.name);
    if (!split) continue;
    const group = byName.get(split.name) ?? { name: split.name, members: [], createdAt: 0 };
    group.members.push({ language: split.language, campaign: c });
    group.createdAt = Math.max(group.createdAt, c.createdAt);
    byName.set(split.name, group);
  }
  const entries: CampaignListEntry[] = [];
  const placed = new Set<string>();
  for (const c of campaigns) {
    const split = splitLanguage(c.name);
    const group = split ? byName.get(split.name) : undefined;
    if (!split || !group || group.members.length < 2) {
      entries.push({ kind: 'single', campaign: c });
      continue;
    }
    if (placed.has(split.name)) continue;
    placed.add(split.name);
    group.members.sort(
      (a, b) =>
        languageRank(a.language) - languageRank(b.language) || a.language.localeCompare(b.language),
    );
    entries.push({ kind: 'group', group });
  }
  return entries;
}

/** How many members have each status, in the order statuses first appear. */
export function statusCounts(group: CampaignGroup): { status: CampaignStatus; count: number }[] {
  const counts = new Map<CampaignStatus, number>();
  for (const m of group.members) {
    counts.set(m.campaign.status, (counts.get(m.campaign.status) ?? 0) + 1);
  }
  return [...counts].map(([status, count]) => ({ status, count }));
}
